import { useAuthStore } from '@/stores/auth'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

const SETTINGS_STORAGE_KEY = 'ghina-trading-settings'

// ======================= Types =======================

export interface TradingSettings {
    buyAmounts: number[]      // e.g., [0.1, 0.25, 0.5, 1.0]
    sellPercents: number[]    // e.g., [25, 50, 75, 100]
    defaultBuyAmount: number  // Default amount for quick buy
    defaultSlippageBps: number // Default slippage (e.g., 500 = 5%)
    defaultTipSol: number      // Default tip (e.g., 0.0015)
}

export interface UserWallet {
    id: string
    name: string
    address: string
    balance_sol: number
    can_trade: boolean
}

export interface Position {
    mint: string
    symbol: string
    name: string
    balance: number           // Token balance
    avg_buy_sol: number       // Average buy price in SOL
    total_bought_sol: number  // Total SOL spent buying
    total_sold_sol: number    // Total SOL received selling
}

export interface TradeStatus {
    loading: boolean
    success?: boolean
    error?: string
    signature?: string
}

// ======================= Default Settings =======================

const DEFAULT_SETTINGS: TradingSettings = {
    buyAmounts: [0.1, 0.25, 0.5, 1.0],
    sellPercents: [25, 50, 75, 100],
    defaultBuyAmount: 0.1,
    defaultSlippageBps: 500,
    defaultTipSol: 0.0015,
}

// ======================= Store =======================

export const useTradingStore = defineStore('trading', () => {
    const authStore = useAuthStore()

    // State
    const wallets = ref<UserWallet[]>([])
    const activeWalletId = ref<string | null>(null)
    const settings = ref<TradingSettings>(loadSettings())
    const positions = ref<Position[]>(loadPositions())
    const tradeStatuses = ref<Map<string, TradeStatus>>(new Map()) // mint -> status

    // WebSocket and API config
    const apiUrl = import.meta.env.VITE_MURSHID_API_URL
    const apiKey = import.meta.env.VITE_MURSHID_REST_API_KEY

    // Computed
    const activeWallet = computed(() =>
        wallets.value.find(w => w.id === activeWalletId.value) || null
    )

    const tradeableWallets = computed(() =>
        wallets.value.filter(w => w.can_trade)
    )

    const userId = computed(() => authStore.user?.id || 'default-user')

    // ======================= Settings Persistence =======================

    function loadSettings(): TradingSettings {
        try {
            const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
            if (stored) {
                return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) }
            }
        } catch (e) {
            console.warn('Failed to load trading settings:', e)
        }
        return { ...DEFAULT_SETTINGS }
    }

    function saveSettings() {
        try {
            localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings.value))
        } catch (e) {
            console.warn('Failed to save trading settings:', e)
        }
    }

    // Auto-save settings on change
    watch(settings, saveSettings, { deep: true })

    // ======================= Positions Persistence =======================

    function loadPositions(): Position[] {
        try {
            const stored = localStorage.getItem('ghina-positions')
            if (stored) {
                return JSON.parse(stored)
            }
        } catch (e) {
            console.warn('Failed to load positions:', e)
        }
        return []
    }

    function savePositions() {
        try {
            localStorage.setItem('ghina-positions', JSON.stringify(positions.value))
        } catch (e) {
            console.warn('Failed to save positions:', e)
        }
    }

    watch(positions, savePositions, { deep: true })

    // ======================= Wallet Management =======================

    async function fetchWallets() {
        if (!apiUrl || !apiKey) return

        try {
            const response = await fetch(
                `${apiUrl}/api/portfolio/wallets?user_id=${userId.value}`,
                { headers: { 'Authorization': `Bearer ${apiKey}` } }
            )
            if (response.ok) {
                const data = await response.json() as { wallets?: UserWallet[] }
                if (data.wallets) {
                    wallets.value = data.wallets
                    // Set first tradeable wallet as active if none selected
                    if (!activeWalletId.value && tradeableWallets.value.length > 0) {
                        activeWalletId.value = tradeableWallets.value[0].id
                    }
                }
            }
        } catch (e) {
            console.error('Failed to fetch wallets:', e)
        }
    }

    function setActiveWallet(walletId: string) {
        activeWalletId.value = walletId
        // Also save to localStorage
        try {
            localStorage.setItem('ghina-active-wallet', walletId)
        } catch { /* ignore */ }
    }

    function loadActiveWallet() {
        try {
            const stored = localStorage.getItem('ghina-active-wallet')
            if (stored) activeWalletId.value = stored
        } catch { /* ignore */ }
    }

    // ======================= Trade Execution =======================

    // ======================= Trade Execution =======================

    function getTradeStatus(mint: string): TradeStatus {
        return tradeStatuses.value.get(mint) || { loading: false }
    }

    function setTradeStatus(mint: string, status: TradeStatus) {
        tradeStatuses.value.set(mint, status)
        // Force reactivity
        tradeStatuses.value = new Map(tradeStatuses.value)
    }

    function clearTradeStatus(mint: string) {
        tradeStatuses.value.delete(mint)
        tradeStatuses.value = new Map(tradeStatuses.value)
    }

    async function executeBuy(
        mint: string,
        solAmount?: number,
        slippageBps?: number,
        tipSol?: number
    ): Promise<boolean> {
        if (!activeWallet.value) {
            console.error('No active wallet selected')
            return false
        }

        if (!apiUrl || !apiKey) {
            console.error('API not configured')
            return false
        }

        const amount = solAmount ?? settings.value.defaultBuyAmount
        const slippage = slippageBps ?? settings.value.defaultSlippageBps
        const tip = tipSol ?? settings.value.defaultTipSol
        const tipLamports = Math.floor(tip * 1_000_000_000)

        setTradeStatus(mint, { loading: true })

        try {
            const response = await fetch(
                `${apiUrl}/api/portfolio/trade?user_id=${userId.value}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${apiKey}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        wallet_id: activeWallet.value.id,
                        token_mint: mint,
                        trade_type: 'buy',
                        sol_amount: amount,
                        slippage_bps: slippage,
                        tip_lamports: tipLamports
                    }),
                }
            )

            const result = await response.json() as { success: boolean; signature?: string; message?: string }

            if (result.success) {
                setTradeStatus(mint, { loading: false, success: true, signature: result.signature })

                // Update local position
                const existing = positions.value.find(p => p.mint === mint)
                if (existing) {
                    existing.balance += (amount / 0.2) // Approximating token amount based on price (FIXME: need real price)
                    existing.total_bought_sol += amount
                    existing.avg_buy_sol = existing.total_bought_sol / existing.balance // refined later
                } else {
                    positions.value.push({
                        mint,
                        symbol: mint.slice(0, 4), // Placeholder, should fetch metadata
                        name: 'Unknown',
                        balance: 1, // Placeholder
                        avg_buy_sol: amount,
                        total_bought_sol: amount,
                        total_sold_sol: 0
                    })
                }

                // Clear status after 3 seconds
                setTimeout(() => clearTradeStatus(mint), 3000)
                return true
            } else {
                setTradeStatus(mint, { loading: false, success: false, error: result.message })
                setTimeout(() => clearTradeStatus(mint), 5000)
                return false
            }
        } catch (e) {
            const error = e instanceof Error ? e.message : 'Trade failed'
            setTradeStatus(mint, { loading: false, success: false, error })
            setTimeout(() => clearTradeStatus(mint), 5000)
            return false
        }
    }

    async function executeSell(
        mint: string,
        percentage?: number,
        slippageBps?: number,
        tipSol?: number
    ): Promise<boolean> {
        if (!activeWallet.value) {
            console.error('No active wallet selected')
            return false
        }

        if (!apiUrl || !apiKey) {
            console.error('API not configured')
            return false
        }

        const pct = percentage ?? 100
        const slippage = slippageBps ?? settings.value.defaultSlippageBps
        const tip = tipSol ?? settings.value.defaultTipSol
        const tipLamports = Math.floor(tip * 1_000_000_000)

        setTradeStatus(mint, { loading: true })

        try {
            const response = await fetch(
                `${apiUrl}/api/portfolio/trade?user_id=${userId.value}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${apiKey}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        wallet_id: activeWallet.value.id,
                        token_mint: mint,
                        trade_type: 'sell',
                        percentage: pct,
                        slippage_bps: slippage,
                        tip_lamports: tipLamports
                    }),
                }
            )

            const result = await response.json() as { success: boolean; signature?: string; message?: string }

            if (result.success) {
                setTradeStatus(mint, { loading: false, success: true, signature: result.signature })

                // Update local position
                const existing = positions.value.find(p => p.mint === mint)
                if (existing) {
                    const sellRatio = pct / 100
                    // We don't know exact sold amount in SOL without price, but we can track percentage balance reduction
                    // For now, let's assume we just reduce balance by percentage
                    existing.balance = existing.balance * (1 - sellRatio)
                    // We don't increment total_bought_sol, but we should track total_sold_sol if we knew amount
                    // Since we don't know amount, we'll leave it for now or needs price data
                }

                setTimeout(() => clearTradeStatus(mint), 3000)
                return true
            } else {
                setTradeStatus(mint, { loading: false, success: false, error: result.message })
                setTimeout(() => clearTradeStatus(mint), 5000)
                return false
            }
        } catch (e) {
            const error = e instanceof Error ? e.message : 'Trade failed'
            setTradeStatus(mint, { loading: false, success: false, error })
            setTimeout(() => clearTradeStatus(mint), 5000)
            return false
        }
    }

    // ======================= Settings Updates =======================

    function updateBuyAmounts(amounts: number[]) {
        settings.value.buyAmounts = amounts
    }

    function updateSellPercents(percents: number[]) {
        settings.value.sellPercents = percents
    }

    function updateDefaultBuyAmount(amount: number) {
        settings.value.defaultBuyAmount = amount
    }

    function updateSlippage(bps: number) {
        settings.value.defaultSlippageBps = bps
    }

    // ======================= Initialize =======================

    function initialize() {
        loadActiveWallet()
        fetchWallets()
    }

    return {
        // State
        wallets,
        activeWalletId,
        activeWallet,
        tradeableWallets,
        settings,
        positions,

        // Trade status
        getTradeStatus,
        clearTradeStatus,

        // Actions
        initialize,
        fetchWallets,
        setActiveWallet,
        executeBuy,
        executeSell,

        // Settings
        updateBuyAmounts,
        updateSellPercents,
        updateDefaultBuyAmount,
        updateSlippage,
    }
})
